import { create } from 'zustand'
import { getAuth } from 'firebase/auth'
import Database from '../types/Database'
import {
  submitRating as serviceSubmitRating,
  getPendingRatees,
  PendingRatee
} from '../services/ratingService'

interface RatingsStoreState {
  pendingRatings: Record<string, PendingRatee[]> // diveId -> ratees not yet rated
  submitting: boolean
  error: string | null
  submitRating: (input: Database.SubmitRatingInput) => Promise<void>
  loadPendingRatings: (diveId: string) => Promise<void>
  clearError: () => void
  reset: () => void
}

export const useRatingsStore = create<RatingsStoreState>((set, get) => ({
  pendingRatings: {},
  submitting: false,
  error: null,

  submitRating: async (input: Database.SubmitRatingInput) => {
    const auth = getAuth()
    const currentUser = auth.currentUser

    if (!currentUser) {
      set({ error: 'Please sign in to continue' })
      return
    }

    set({ submitting: true, error: null })

    try {
      await serviceSubmitRating(input)

      // On success, remove the rateeId from pendingRatings[diveId]
      const { pendingRatings } = get()
      const currentPending = pendingRatings[input.diveId] ?? []
      const updatedPending = currentPending.filter(
        ratee => ratee.id !== input.rateeId
      )

      set({
        pendingRatings: {
          ...pendingRatings,
          [input.diveId]: updatedPending
        },
        submitting: false
      })
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to submit rating. Please try again.'
      set({ error: message, submitting: false })
    }
  },

  loadPendingRatings: async (diveId: string) => {
    const auth = getAuth()
    const currentUser = auth.currentUser

    if (!currentUser) {
      set({ error: 'Please sign in to continue' })
      return
    }

    try {
      const ratees = await getPendingRatees(diveId, currentUser.uid)
      const { pendingRatings } = get()

      set({
        pendingRatings: {
          ...pendingRatings,
          [diveId]: ratees
        }
      })
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to load pending ratings.'
      set({ error: message })
    }
  },

  clearError: () => set({ error: null }),

  reset: () =>
    set({
      pendingRatings: {},
      submitting: false,
      error: null
    })
}))
